/**
 * ReferralHistoryTable component - displays paginated referral history with sorting
 */
import React, { useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Popover,
  Skeleton,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { IconDownload, IconInfoCircle } from "@tabler/icons-react";
import { useReferralStore } from "@/stores/referralStore";
import { ReferralStatistics } from "../ReferralStatistics";
import ReferralExportModal from "../ReferralExportModal";
import {
  EmptyReferralState,
  ReferralTableHeader,
  ReferralTableRow,
  ReferralTablePagination,
} from "./components";
import { useSorting } from "./hooks";

export const ReferralHistoryTable: React.FC = () => {
  const dashboard = useReferralStore((state) => state.dashboard);
  const isLoading = useReferralStore((state) => state.isLoadingDashboard);
  const expandedRows = useReferralStore((state) => state.expandedRows);
  const toggleRowExpansion = useReferralStore(
    (state) => state.toggleRowExpansion
  );
  const currentPage = useReferralStore((state) => state.currentPage);
  const setCurrentPage = useReferralStore((state) => state.setCurrentPage);

  const [exportModalOpened, setExportModalOpened] = useState(false);

  const { sortField, sortDirection, sortedEntries, handleSort } = useSorting(
    dashboard?.entries
  );

  const tableRows = sortedEntries.map((item) => (
    <ReferralTableRow
      key={item.id}
      item={item}
      isExpanded={expandedRows.has(item.id)}
      onToggleExpand={() => toggleRowExpansion(item.id)}
    />
  ));

  return (
    <Card p="lg" radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="xs" align="center">
            <Text fw={600} size="lg">
              Referral History
            </Text>
            {dashboard && dashboard.pagination.total > 0 && (
              <Badge size="lg" variant="light" color="blue">
                {dashboard.pagination.total}{" "}
                {dashboard.pagination.total === 1 ? "Referral" : "Referrals"}
              </Badge>
            )}
            <Popover width={400} position="bottom-start" withArrow shadow="md">
              <Popover.Target>
                <ActionIcon variant="subtle" color="gray" size="sm">
                  <IconInfoCircle size={18} />
                </ActionIcon>
              </Popover.Target>
              <Popover.Dropdown>
                <Text size="sm">
                  A complete list of all people who have signed up using your
                  referral link, including their payment status and rewards
                </Text>
              </Popover.Dropdown>
            </Popover>
          </Group>
          {(dashboard?.entries || []).length > 0 && (
            <Button
              leftSection={<IconDownload size={16} />}
              variant="light"
              size="sm"
              onClick={() => setExportModalOpened(true)}
            >
              Export CSV
            </Button>
          )}
        </Group>

        {isLoading ? (
          <Stack gap="xs">
            <Skeleton height={40} />
            <Skeleton height={200} />
          </Stack>
        ) : (dashboard?.entries || []).length === 0 ? (
          <EmptyReferralState />
        ) : (
          <>
            {/* Statistics Cards */}
            <ReferralStatistics />
            <Table
              striped
              highlightOnHover
              withTableBorder
              withColumnBorders
              style={{
                borderCollapse: "collapse",
              }}
            >
              <ReferralTableHeader
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <Table.Tbody>{tableRows}</Table.Tbody>
            </Table>
            <ReferralTablePagination
              currentPage={currentPage}
              totalPages={dashboard?.pagination.totalPages || 1}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </Stack>

      {/* Export Modal */}
      <ReferralExportModal
        opened={exportModalOpened}
        onClose={() => setExportModalOpened(false)}
      />
    </Card>
  );
};
